import { IconEdit, IconTrash, IconX, IconPlus, IconCheck, IconEye, IconDownload } from "@tabler/icons-react";
import { Kruzok, Rocnik } from "../Admin"
import { useState, useRef, useEffect } from "react";
import { authenticatedFetch } from "../../../utils/auth";

export default function Kruzky({ kruzky, setKruzky }: { kruzky: Kruzok[], setKruzky: React.Dispatch<React.SetStateAction<Kruzok[]>> }) {
    const [kurzokPopUp, setKruzokPopup] = useState<boolean>(false);
    const [editingKruzok, setEditingKruzok] = useState<Kruzok | null>(null);
    // default to unlimited capacity for new kruzok
    const [isCapacityLimited, setIsCapacityLimited] = useState<boolean>(false);
    const [genderMale, setGenderMale] = useState<boolean>(false);
    const [genderFemale, setGenderFemale] = useState<boolean>(false);
    const [isStretko, setIsStretko] = useState<boolean>(false);
    // default to requiring zapisne for new kruzok
    const [requiresZapisne, setRequiresZapisne] = useState<boolean>(true);
    const [schoolYears, setSchoolYears] = useState<Rocnik[]>([]);
    const [selectedSchoolYears, setSelectedSchoolYears] = useState<string[]>([]);
    const capacityInputRef = useRef<HTMLInputElement>(null);

    // Fetch school years when component mounts
    useEffect(() => {
        const fetchSchoolYears = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_PATH}/api/vseob/rocnik`);
                if (response.ok) {
                    const data = await response.json();



                    setSchoolYears(data.data);
                }
            } catch (error) {
                console.error('Error fetching school years:', error);
            }

        };
        fetchSchoolYears();
    }, []);

    const getGenderDisplay = (gender: string) => {
        switch (gender) {
            case 'MALE':
                return '♂️ Chlapci';
            case 'FEMALE':
                return '♀️ Dievčatá';
            case 'BOTH':
                return '👫 Všetci';
            default:
                return gender;
        }
    };

    const getSchoolYearTypeDisplay = (type: string) => {
        switch (type) {
            case 'PRESCHOOL':
                return 'Materská škola';
            case 'PRIMARY':
                return 'Základná škola';
            case 'HIGHSCHOOL':
                return 'Stredná škola';
            default:
                return type;
        }
    };

    const getSchoolYearTypeColors = (type: string) => {
        switch (type) {
            case 'PRIMARY':
                return 'bg-blue-100 text-blue-800';
            case 'HIGHSCHOOL':
                return 'bg-green-100 text-green-800';
            case 'PRESCHOOL':
                return 'bg-yellow-100 text-yellow-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const getTypeDisplay = (type: string) => {
        switch (type) {
            case 'TRIH':
                return '3 Hôrky';
            case 'BERN':
                return 'CZŠ Bernolákova';
            default:
                return type;
        }
    };

    // Applicant status helpers for modal
    const getStatusOrder = (status: string) => {
        switch (status) {
            case 'ACCEPTED': return 0;
            case 'GENERATED': return 1;
            case 'DRAFT': return 2;
            default: return 3;
        }
    };

    const getStatusDisplay = (status: string) => {
        switch (status) {
            case 'ACCEPTED': return 'Prijaté';
            case 'GENERATED': return 'Odoslané';
            case 'DRAFT': return 'Koncept';
            default: return status;
        }
    };

    const getStatusColors = (status: string) => {
        switch (status) {
            case 'ACCEPTED': return 'bg-green-100 text-green-800';
            case 'GENERATED': return 'bg-blue-100 text-blue-800';
            case 'DRAFT': return 'bg-yellow-100 text-yellow-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    // Sort helper: non-stretko kruzky first, then stretko; within groups sort by name
    const sortKruzkyByGroup = (list: Kruzok[]) => {
        return list.slice().sort((a, b) => {
            if ((a as any).isStretko === (b as any).isStretko) {
                return a.name.localeCompare(b.name, 'sk');
            }
            return (a as any).isStretko ? 1 : -1;
        });
    };

    const onSubmitKruzok = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const formData = new FormData(e.currentTarget);
        const name = formData.get("name") as string;
        const description = formData.get("description") as string;
        const price = parseInt(formData.get("price") as string);
        const capacityValue = formData.get("capacity") as string;
        const capacity = isCapacityLimited ? parseInt(capacityValue) : -1;
        const time = formData.get("time") as string;
        const type = formData.get("type") as string;

        // Determine gender based on checkboxes
        let gender = '';
        if (genderMale && genderFemale) {
            gender = 'BOTH';
        } else if (genderMale) {
            gender = 'MALE';
        } else if (genderFemale) {
            gender = 'FEMALE';
        }

        const isCapacityLimitedValue = isCapacityLimited;

        if (!name || (!price && price !== 0) || !time || !type || !gender) {
            alert("Všetky povinné polia musia byť vyplnené! Musíte vybrať aspoň jedno pohlavie.");
            return;
        }

        if (isCapacityLimitedValue && (!capacityValue || parseInt(capacityValue) <= 0)) {
            alert("Kapacita musí byť zadaná a väčšia ako 0, ak je obmedzená!");
            return;
        }

        if (selectedSchoolYears.length === 0) {
            alert("Musíte vybrať aspoň jeden ročník!");
            return;
        }

        try {
            const url = editingKruzok
                ? `${process.env.REACT_APP_PATH}/api/kruzok/${editingKruzok.id}`
                : `${process.env.REACT_APP_PATH}/api/kruzok/add`;

            const method = editingKruzok ? "PUT" : "POST";

            const response = await authenticatedFetch(url, {
                method: method,
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    name,
                    description,
                    price,
                    capacity,
                    time,
                    type,
                    gender,
                    isCapacityLimited: isCapacityLimitedValue,
                    isStretko,
                    requiresZapisne,
                    schoolYearIds: selectedSchoolYears
                }),
            });

            if (!response.ok) {
                throw new Error(`Failed to ${editingKruzok ? 'update' : 'add'} kruzok`);
            }

            const data = await response.json();

            if (editingKruzok) {
                // Update existing kruzok in the list
                const updatedKruzky = kruzky.map(k => k.id === editingKruzok.id ? data.data : k);
                setKruzky(updatedKruzky);
                setEditingKruzok(null);
            } else {
                // Add new kruzok to the list
                setKruzky([...kruzky, data.data]);
            }

            setKruzokPopup(false);
            // reset to defaults for next "add": unlimited capacity and zapisne required
            setIsCapacityLimited(false);
            setRequiresZapisne(true);
            (e.target as HTMLFormElement).reset();
        } catch (error) {
            console.error(`Error ${editingKruzok ? 'updating' : 'adding'} kruzok:`, error);
            alert(`Chyba pri ${editingKruzok ? 'úprave' : 'pridávaní'} krúžku`);
        }
    };

    const deleteKruzok = async (id: string) => {
        if (!window.confirm("Ste si istí, že chcete vymazať tento krúžok?")) {
            return;
        }

        try {
            const response = await authenticatedFetch(`${process.env.REACT_APP_PATH}/api/kruzok/${id}`, {
                method: "DELETE",
            });

            if (!response.ok) {
                throw new Error("Failed to delete kruzok");
            }

            const updatedKruzky = kruzky.filter(k => k.id !== id);
            setKruzky(updatedKruzky);
        } catch (error) {
            console.error("Error deleting kruzok:", error);
            alert("Chyba pri mazaní krúžku");
        }
    };

    const openEditPopup = (kruzok: Kruzok) => {
        setEditingKruzok(kruzok);
        setIsCapacityLimited(kruzok.capacity !== -1);
        setIsStretko(kruzok.isStretko || false);
        setRequiresZapisne(kruzok.requiresZapisne || false);

        // Set gender checkboxes based on kruzok gender
        if (kruzok.gender === 'BOTH') {
            setGenderMale(true);
            setGenderFemale(true);
        } else if (kruzok.gender === 'MALE') {
            setGenderMale(true);
            setGenderFemale(false);
        } else if (kruzok.gender === 'FEMALE') {
            setGenderMale(false);
            setGenderFemale(true);
        } else {
            setGenderMale(false);
            setGenderFemale(false);
        }

        // Set selected school years
        if (kruzok.schoolYear) {
            setSelectedSchoolYears(kruzok.schoolYear.map(sy => sy.id));
        } else {
            setSelectedSchoolYears([]);
        }

        setKruzokPopup(true);
    };

    const closePopup = () => {
        setKruzokPopup(false);
        setEditingKruzok(null);
        // defaults for next add
        setIsCapacityLimited(false);
        setGenderMale(false);
        setGenderFemale(false);
        setIsStretko(false);
        setRequiresZapisne(true);
        setSelectedSchoolYears([]);
    };

    // Applicants modal state
    const [applicantsModalOpen, setApplicantsModalOpen] = useState<boolean>(false);
    const [applicants, setApplicants] = useState<any[]>([]);
    const [loadingApplicants, setLoadingApplicants] = useState<boolean>(false);
    const [applicantsError, setApplicantsError] = useState<string | null>(null);
    const [currentKruzok, setCurrentKruzok] = useState<Kruzok | null>(null);

    const openApplicantsModal = async (kruzokId: string) => {
        const kruzok = kruzky.find(k => k.id === kruzokId);
        setCurrentKruzok(kruzok || null);
        setApplicantsModalOpen(true);
        setLoadingApplicants(true);
        setApplicantsError(null);
        try {
            const res = await authenticatedFetch(`${process.env.REACT_APP_PATH}/api/kruzok/${kruzokId}/applicants`);
            if (!res.ok) throw new Error('Failed to fetch applicants');
            const data = await res.json();

            setApplicants(data.data || []);
        } catch (err: any) {
            console.error('Error fetching applicants:', err);
            setApplicantsError('Chyba pri načítaní prihlášok');
        } finally {
            setLoadingApplicants(false);
        }
    };

    const exportApplicants = () => {
        if (!currentKruzok || applicants.length === 0) return;

        // Create HTML table that Excel can open as xlsx
        let htmlContent = `
            <html>
                <head>
                    <meta charset="utf-8">
                </head>
                <body>
                    <table border="1">
                        <tr><td><strong>Krúžok:</strong></td><td>${currentKruzok.name}</td><td></td></tr>
                        <tr><td><strong>Čas:</strong></td><td>${currentKruzok.time}</td><td></td></tr>
                        <tr><td></td><td></td><td></td></tr>
                        <tr><td><strong>Meno</strong></td><td><strong>Priezvisko</strong></td><td><strong>Ročník</strong></td></tr>
        `;

        // Add applicant rows
        applicants.forEach(applicant => {
            htmlContent += `<tr>
                <td>${applicant.person?.firstName || ''}</td>
                <td>${applicant.person?.lastName || ''}</td>
                <td>${applicant.person?.schoolyear?.name || ''}</td>
            </tr>`;
        });

        htmlContent += `
                    </table>
                </body>
            </html>
        `;

        // Create Excel-compatible file
        const blob = new Blob([htmlContent], {
            type: 'application/vnd.ms-excel;charset=utf-8'
        });

        const link = document.createElement('a');
        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `${currentKruzok.name.replace(/[^a-z0-9áčďéíľňóšťúýž]/gi, '_')}_prihlásení.xls`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };




    return (
        <>
            {kruzky.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 w-[95vw] absolute left-[2.5vw]">
                    <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex justify-between">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">Krúžky</h3>
                            <p className="text-sm text-gray-600 mt-1">Spravujte krúžky a ich vlastnosti</p>
                        </div>

                        <button
                            onClick={() => {
                                // Open add popup with defaults for new kruzok
                                setEditingKruzok(null);
                                setGenderMale(false);
                                setGenderFemale(false);
                                setIsCapacityLimited(false); // unlimited by default
                                setIsStretko(false);
                                setRequiresZapisne(true); // require zapisne by default
                                setSelectedSchoolYears([]);
                                setKruzokPopup(true);
                            }}
                            className="inline-flex items-center px-1 py-1 md:px-4 md:py-2 border border-transparent *:text-sm *:font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-150"
                        >
                            <IconPlus size={16} className="mr-px md:mr-2" />
                            <span>Pridať</span>
                            <span className="hidden md:inline">&nbsp;krúžok</span>
                        </button>
                    </div>



                    <div className="overflow-x-auto table-responsive">
                        <table className="w-full min-w-[800px]">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Názov</th>
                                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cena</th>
                                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kapacita</th>
                                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pohlavie</th>
                                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Čas</th>
                                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Typ</th>
                                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ročníky</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Akcie</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {sortKruzkyByGroup(kruzky).map((kruzok: Kruzok, index: number) => (
                                    <tr key={kruzok.id || index} className="hover:bg-gray-50 transition-colors duration-150">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center gap-2">
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900">{kruzok.name}</div>
                                                    {kruzok.description && (
                                                        <div className="text-sm text-gray-500">{kruzok.description}</div>
                                                    )}
                                                </div>
                                                {kruzok.isStretko && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                                        Stretko
                                                    </span>
                                                )}
                                                {!kruzok.requiresZapisne && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                                                        Bez zápisného
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                +{kruzok.price}€
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {kruzok.capacity === -1 ? (
                                                <div className="flex flex-col">
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                                        ∞ Neobmedzená
                                                    </span>
                                                    {kruzok.currentRegistrations !== undefined && (
                                                        <span className="text-xs text-gray-600 mt-1 underline cursor-pointer" onClick={() => openApplicantsModal(kruzok.id)}>
                                                            Prihlásení: {kruzok.currentRegistrations}
                                                        </span>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="flex flex-col">
                                                    <span className={`inline-flex   items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${kruzok.currentRegistrations !== undefined && kruzok.currentRegistrations >= kruzok.capacity
                                                        ? 'bg-red-100 text-red-800'
                                                        : kruzok.currentRegistrations !== undefined && kruzok.currentRegistrations >= kruzok.capacity * 0.8
                                                            ? 'bg-yellow-100 text-yellow-800'
                                                            : 'bg-blue-100 text-blue-800'
                                                        }`}>
                                                        👥 {kruzok.capacity} detí
                                                    </span>
                                                    {kruzok.currentRegistrations !== undefined && (
                                                        <span className={`text-xs mt-1 ${kruzok.currentRegistrations >= kruzok.capacity
                                                            ? 'text-red-600 font-semibold underline cursor-pointer'
                                                            : kruzok.currentRegistrations >= kruzok.capacity * 0.8
                                                                ? 'text-yellow-600 underline cursor-pointer'
                                                                : 'text-green-600 underline cursor-pointer'
                                                            }`}
                                                            onClick={() => openApplicantsModal(kruzok.id)}
                                                        >
                                                            {kruzok.currentRegistrations}/{kruzok.capacity} prihlásených
                                                            {kruzok.currentRegistrations >= kruzok.capacity && ' (Plné!)'}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                                {getGenderDisplay(kruzok.gender)}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">{kruzok.time}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${kruzok.type === 'TRIH' ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800'}`}>
                                                {getTypeDisplay(kruzok.type)}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-wrap gap-1 ">
                                                {kruzok.schoolYear && kruzok.schoolYear.length > 0 ? (
                                                    kruzok.schoolYear
                                                        .sort((a, b) => a.order - b.order)
                                                        .map((schoolYear) => (
                                                            <span
                                                                key={schoolYear.id}
                                                                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getSchoolYearTypeColors(schoolYear.type)}`}
                                                                title={`${schoolYear.name} - ${getSchoolYearTypeDisplay(schoolYear.type)}`}
                                                            >
                                                                {schoolYear.name}
                                                            </span>
                                                        ))
                                                ) : (
                                                    <span className="text-xs text-gray-400 italic">
                                                        Žiadne ročníky
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <div className="flex items-center justify-end space-x-2">
                                                <button
                                                    onClick={() => openEditPopup(kruzok)}
                                                    className="inline-flex items-center p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors duration-150"
                                                    title="Upraviť"
                                                >
                                                    <IconEdit size={16} />
                                                </button>
                                                <button
                                                    onClick={() => deleteKruzok(kruzok.id)}
                                                    className="inline-flex items-center p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors duration-150"
                                                    title="Vymazať"
                                                >
                                                    <IconTrash size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div >
            )
            }



            {
                kurzokPopUp &&
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-6">
                                <h2 className="text-xl font-semibold text-gray-900">
                                    {editingKruzok ? 'Upraviť krúžok' : 'Pridať krúžok'}
                                </h2>
                                <button
                                    onClick={closePopup}
                                    className="text-gray-400 hover:text-gray-600 transition-colors duration-150"
                                >
                                    <IconX size={20} />
                                </button>
                            </div>
                            <form onSubmit={onSubmitKruzok} className="space-y-6">
                                {/* Hidden inputs for state values */}
                                <input type="hidden" name="isCapacityLimited" value={isCapacityLimited ? "true" : "false"} />
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-4">
                                        <div>
                                            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                                                Názov
                                            </label>
                                            <input
                                                id="name"
                                                name="name"
                                                type="text"
                                                placeholder="Názov krúžku"
                                                defaultValue={editingKruzok?.name || ''}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                                                Popis
                                            </label>
                                            <textarea
                                                id="description"
                                                name="description"
                                                placeholder="Popis krúžku"
                                                rows={3}
                                                defaultValue={editingKruzok?.description || ''}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1">
                                                Príplatok (€)
                                            </label>
                                            <input
                                                id="price"
                                                name="price"
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                placeholder="0.00"
                                                defaultValue={editingKruzok?.price || 0}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                required
                                            />
                                        </div>
                                        {/* Capacity Section */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Obmedzená kapacita krúžku
                                            </label>
                                            <div className="flex gap-4 items-center">
                                                <div className="flex gap-8">
                                                    <div
                                                        onClick={() => {
                                                            const newLimitedState = !isCapacityLimited;
                                                            setIsCapacityLimited(newLimitedState);
                                                            if (editingKruzok) setEditingKruzok({ ...editingKruzok, capacity: !newLimitedState ? -1 : 0 });
                                                            // Clear input value when switching to unlimited
                                                            if (!newLimitedState && capacityInputRef.current) {
                                                                capacityInputRef.current.value = '';
                                                            }
                                                        }}
                                                        className={`size-8 border-2 flex items-center justify-center cursor-pointer border-blue-600 rounded ${isCapacityLimited ? 'bg-blue-600' : 'bg-white'}`}>
                                                        {isCapacityLimited ? <IconCheck color="white" /> : null}
                                                    </div>
                                                </div>

                                                <input
                                                    ref={capacityInputRef}
                                                    id="capacity"
                                                    name="capacity"
                                                    type="number"
                                                    min="1"
                                                    placeholder={!isCapacityLimited ? 'Neobmedzená' : 'napr. 15'}
                                                    defaultValue={editingKruzok && editingKruzok.capacity !== -1 ? editingKruzok.capacity : ''}
                                                    className="disabled:cursor-not-allowed w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                                    disabled={!isCapacityLimited}
                                                />
                                                {/* Hidden input for unlimited capacity */}
                                                {!isCapacityLimited && (
                                                    <input type="hidden" name="capacity" value="-1" />
                                                )}
                                            </div>

                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Pre koho je krúžok určený
                                            </label>
                                            <div className="flex gap-8">
                                                <div className="flex items-center gap-2">
                                                    <div
                                                        onClick={() => setGenderMale(!genderMale)}
                                                        className={`size-8 border-2 flex items-center justify-center cursor-pointer border-blue-600 rounded ${genderMale ? 'bg-blue-600' : 'bg-white'}`}>
                                                        {genderMale ? <IconCheck color="white" /> : null}
                                                    </div>
                                                    <p className="text-gray-700">Chlapci</p>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <div
                                                        onClick={() => setGenderFemale(!genderFemale)}
                                                        className={`size-8 border-2 flex items-center justify-center cursor-pointer border-blue-600 rounded ${genderFemale ? 'bg-blue-600' : 'bg-white'}`}>
                                                        {genderFemale ? <IconCheck color="white" /> : null}
                                                    </div>
                                                    <p className="text-gray-700">Dievčatá</p>
                                                </div>
                                            </div>
                                            <div className=" text-xs text-gray-500 mt-1">
                                                {genderMale && genderFemale ? 'Krúžok je pre všetky deti' :
                                                    genderMale ? 'Krúžok je len pre chlapcov' :
                                                        genderFemale ? 'Krúžok je len pre dievčatá' :
                                                            'Vyberte aspoň jedno pohlavie'}
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Stretko krúžok
                                            </label>
                                            <div className="flex items-center gap-2">
                                                <div
                                                    onClick={() => setIsStretko(!isStretko)}
                                                    className={`size-8 border-2 flex items-center justify-center cursor-pointer border-blue-600 rounded ${isStretko ? 'bg-blue-600' : 'bg-white'}`}>
                                                    {isStretko ? <IconCheck color="white" /> : null}
                                                </div>
                                                <p className="text-gray-700">Toto je stretko krúžok</p>
                                            </div>
                                            <div className="text-xs text-gray-500 mt-1">
                                                Označte, ak sa jedná o stretko krúžok
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Vyžaduje zápisné
                                            </label>
                                            <div className="flex items-center gap-2">
                                                <div
                                                    onClick={() => setRequiresZapisne(!requiresZapisne)}
                                                    className={`size-8 border-2 flex items-center justify-center cursor-pointer border-blue-600 rounded ${requiresZapisne ? 'bg-blue-600' : 'bg-white'}`}>
                                                    {requiresZapisne ? <IconCheck color="white" /> : null}
                                                </div>
                                                <p className="text-gray-700">Tento krúžok vyžaduje zápisné</p>
                                            </div>
                                            <div className="text-xs text-gray-500 mt-1">
                                                Označte, ak sa za tento krúžok platí zápisné
                                            </div>
                                        </div>

                                        <div>
                                            <label htmlFor="time" className="block text-sm font-medium text-gray-700 mb-1">
                                                Čas
                                            </label>
                                            <input
                                                id="time"
                                                name="time"
                                                type="text"
                                                placeholder="napr. 14:00 - 15:00"
                                                defaultValue={editingKruzok?.time || ''}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
                                                Typ / Lokácia
                                            </label>
                                            <select
                                                id="type"
                                                name="type"
                                                defaultValue={editingKruzok?.type || ''}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                required
                                            >
                                                <option value="">Vyberte typ</option>
                                                <option value="TRIH">3 Hôrky</option>
                                                <option value="BERN">CZŠ Bernoláková</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-3">
                                                Pre ročník(y)
                                            </label>

                                            {/* Bulk select/deselect by type */}
                                            <div className="mb-3 space-y-2">
                                                <div className="flex  gap-2">
                                                    {['PRESCHOOL', 'PRIMARY', 'HIGHSCHOOL'].map((type) => {
                                                        const typeSchoolYears = schoolYears.filter(sy => sy.type === type);
                                                        const allSelected = typeSchoolYears.length > 0 && typeSchoolYears.every(sy => selectedSchoolYears.includes(sy.id));
                                                        const someSelected = typeSchoolYears.some(sy => selectedSchoolYears.includes(sy.id));

                                                        if (typeSchoolYears.length === 0) return null;

                                                        return (
                                                            <button
                                                                key={type}
                                                                type="button"
                                                                onClick={() => {
                                                                    const typeIds = typeSchoolYears.map(sy => sy.id);
                                                                    if (allSelected) {
                                                                        // Deselect all of this type
                                                                        setSelectedSchoolYears(selectedSchoolYears.filter(id => !typeIds.includes(id)));
                                                                    } else {
                                                                        // Select all of this type
                                                                        const newSelected = [...selectedSchoolYears];
                                                                        typeIds.forEach(id => {
                                                                            if (!newSelected.includes(id)) {
                                                                                newSelected.push(id);
                                                                            }
                                                                        });
                                                                        setSelectedSchoolYears(newSelected);
                                                                    }
                                                                }}
                                                                className={`w-1/3 py-1 text-xs rounded-md border transition-colors duration-150 ${allSelected
                                                                    ? 'bg-blue-600 text-white border-blue-600'
                                                                    : someSelected
                                                                        ? 'bg-blue-100 text-blue-700 border-blue-300'
                                                                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                                                    }`}
                                                            >
                                                                {allSelected ? '✓ ' : someSelected ? '◐ ' : ''}
                                                                {getSchoolYearTypeDisplay(type)} ({typeSchoolYears.length})
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>

                                            <div className="space-y-3 overflow-y-auto border border-gray-200 rounded-md p-3 bg-gray-50">
                                                {schoolYears.map((schoolYear) => (
                                                    <div key={schoolYear.id} className="flex items-center gap-2">
                                                        <div
                                                            onClick={() => {
                                                                const isSelected = selectedSchoolYears.includes(schoolYear.id);
                                                                if (isSelected) {
                                                                    setSelectedSchoolYears(selectedSchoolYears.filter(id => id !== schoolYear.id));
                                                                } else {
                                                                    setSelectedSchoolYears([...selectedSchoolYears, schoolYear.id]);
                                                                }
                                                            }}
                                                            className={`size-8 border-2 flex items-center justify-center cursor-pointer border-blue-600 rounded ${selectedSchoolYears.includes(schoolYear.id) ? 'bg-blue-600' : 'bg-white'}`}
                                                        >
                                                            {selectedSchoolYears.includes(schoolYear.id) ? <IconCheck color="white" /> : null}
                                                        </div>
                                                        <div className="flex-1">
                                                            <p className="text-sm font-medium text-gray-900">{schoolYear.name}</p>
                                                            <p className="text-xs text-gray-500">
                                                                {getSchoolYearTypeDisplay(schoolYear.type)} • Poradie: {schoolYear.order}
                                                            </p>
                                                        </div>
                                                    </div>
                                                ))}
                                                {schoolYears.length === 0 && (
                                                    <p className="text-sm text-gray-500 text-center py-4">
                                                        Žiadne ročníky nie sú dostupné
                                                    </p>
                                                )}
                                            </div>
                                            <div className="mt-2 text-xs text-gray-500">
                                                {selectedSchoolYears.length === 0
                                                    ? 'Vyberte aspoň jeden ročník'
                                                    : `Vybraných ${selectedSchoolYears.length} ročníkov`
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex space-x-3 pt-6 border-t border-gray-200">
                                    <button
                                        type="button"
                                        onClick={closePopup}
                                        className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-150"
                                    >
                                        Zrušiť
                                    </button>
                                    <button
                                        type="submit"
                                        className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-150"
                                    >
                                        {editingKruzok ? 'Upraviť krúžok' : 'Pridať krúžok'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            }

            {
                applicantsModalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg shadow-lg max-w-3xl w-full mx-4 max-h-[80vh] overflow-y-auto">
                            <div className="p-4">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-lg font-semibold">Prihlásené osoby</h3>
                                    <div className="flex items-center gap-2">
                                        {applicants.length > 0 && (
                                            <button
                                                onClick={exportApplicants}
                                                className="inline-flex items-center px-3 py-1 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-150"
                                                title="Exportovať zoznam"
                                            >
                                                <IconDownload size={16} className="mr-1" />
                                                Exportovať
                                            </button>
                                        )}
                                        <button onClick={() => { setApplicantsModalOpen(false); setApplicants([]); setCurrentKruzok(null); }} className="text-gray-400 hover:text-gray-600"><IconX size={18} /></button>
                                    </div>
                                </div>

                                {loadingApplicants ? (
                                    <div className="text-center py-6">Načítavam...</div>
                                ) : applicantsError ? (
                                    <div className="text-center text-red-600">{applicantsError}</div>
                                ) : applicants.length === 0 ? (
                                    <div className="text-center py-6 text-gray-600">Žiadne prihlášky pre tento krúžok</div>
                                ) : (
                                    <div className="space-y-3">
                                        {applicants
                                            .slice()
                                            .sort((a, b) => getStatusOrder(a.status) - getStatusOrder(b.status) || a.person?.lastName.localeCompare(b.person?.lastName))
                                            .map((p) => (
                                                <div key={p.id} className="border border-gray-200 rounded p-3 flex justify-between items-center">
                                                    <div className="flex items-start gap-3">
                                                        <div className="flex flex-col">
                                                            <div className="font-medium text-gray-900">{p.person?.lastName} {p.person?.firstName}</div>
                                                            <div className="text-xs text-gray-500">{p.person?.email}</div>
                                                        </div>
                                                        <div>
                                                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColors(p.status)}`}>
                                                                {getStatusDisplay(p.status)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="text-sm text-gray-700">{p.person.schoolyear?.name}</div>
                                                        <div className="text-xs text-gray-500">Vytvorené: {new Date(p.createdAt).toLocaleString('sk-SK')}</div>
                                                    </div>
                                                </div>
                                            ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )
            }
        </>
    );
}